<template>
  <Modal
    :isOpen="isOpen"
    @update:isOpen="$emit('update:isOpen', $event)"
    modalId="customer-details-modal"
    :title="customer ? customer.name : 'Customer'"
    subtitle="Customer details, contacts, notes, and rates."
    maxWidth="6xl"
    @close="onClose"
  >
    <template #content>
      <div class="tw-relative tw--mx-5 tw--my-4 tw-bg-slate-50 tw-p-5">
        <div v-if="$apollo.queries.customer.loading" class="tw-flex tw-items-center tw-justify-center tw-py-10">
          <font-awesome-icon icon="circle-notch" spin class="tw-h-8 tw-w-8 tw-text-slate-500" />
        </div>

        <div v-else-if="customer">
          <div class="tw-space-y-4">
            <div
              class="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white tw-px-4 tw-py-3 sm:tw-px-5">
              <div class="tw-min-w-0">
                <div v-if="!isEditingName" class="tw-flex tw-items-center tw-gap-2">
                  <h2 class="tw-text-lg sm:tw-text-xl tw-font-semibold tw-text-slate-900">
                    {{ customer.name }}
                  </h2>
                  <button type="button"
                    class="tw-inline-flex tw-items-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white/60 tw-px-2.5 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-slate-700 hover:tw-bg-white"
                    @click="editName">
                    Edit
                  </button>
                </div>
                <div v-else class="tw-flex tw-flex-wrap tw-items-end tw-gap-2">
                  <InputField class="tw-w-[320px]" id="CustomerName" label="Customer Name" v-model="tempCustomerName">
                  </InputField>
                  <Button
                    :isGreen="true"
                    :isPrimary="true"
                    class="tw-p-0"
                    icon="check"
                    @click="saveCustomerName"
                    title="Save customer name"
                    aria-label="Save customer name"
                  >
                    Save
                  </Button>
                  <Button
                    :isRed="true"
                    :isSecondary="true"
                    class="tw-p-0"
                    @click="cancelEditName"
                    title="Cancel customer name edit"
                    aria-label="Cancel customer name edit"
                  >
                    Cancel
                  </Button>
                </div>
              </div>

              <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                <div
                  class="tw-inline-flex tw-flex-wrap tw-items-center tw-gap-1 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-slate-100 tw-p-1">
                  <button type="button"
                    class="tw-inline-flex tw-items-center tw-rounded-lg tw-px-3 tw-py-1.5 tw-text-sm tw-font-semibold tw-transition focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-indigo-500 focus-visible:tw-ring-offset-2"
                    @click="activeTab = 'overview'" :class="activeTab === 'overview'
                      ? 'tw-bg-white tw-text-slate-900 tw-shadow-sm'
                      : 'tw-text-slate-600 hover:tw-text-slate-900'">
                    Overview
                  </button>
                  <button type="button"
                    class="tw-inline-flex tw-items-center tw-rounded-lg tw-px-3 tw-py-1.5 tw-text-sm tw-font-semibold tw-transition focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-indigo-500 focus-visible:tw-ring-offset-2"
                    @click="activeTab = 'containers'" :class="activeTab === 'containers'
                      ? 'tw-bg-white tw-text-slate-900 tw-shadow-sm'
                      : 'tw-text-slate-600 hover:tw-text-slate-900'">
                    Containers
                  </button>
                  <button type="button"
                    class="tw-inline-flex tw-items-center tw-rounded-lg tw-px-3 tw-py-1.5 tw-text-sm tw-font-semibold tw-transition focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-indigo-500 focus-visible:tw-ring-offset-2"
                    @click="activeTab = 'rates'" :class="activeTab === 'rates'
                      ? 'tw-bg-white tw-text-slate-900 tw-shadow-sm'
                      : 'tw-text-slate-600 hover:tw-text-slate-900'">
                    Rates & Billing
                  </button>
                  <button type="button"
                    class="tw-inline-flex tw-items-center tw-rounded-lg tw-px-3 tw-py-1.5 tw-text-sm tw-font-semibold tw-transition focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-indigo-500 focus-visible:tw-ring-offset-2"
                    @click="activeTab = 'contacts'" :class="activeTab === 'contacts'
                      ? 'tw-bg-white tw-text-slate-900 tw-shadow-sm'
                      : 'tw-text-slate-600 hover:tw-text-slate-900'">
                    Contacts
                  </button>
                  <button type="button"
                    class="tw-inline-flex tw-items-center tw-rounded-lg tw-px-3 tw-py-1.5 tw-text-sm tw-font-semibold tw-transition focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-indigo-500 focus-visible:tw-ring-offset-2"
                    @click="activeTab = 'notes'" :class="activeTab === 'notes'
                      ? 'tw-bg-white tw-text-slate-900 tw-shadow-sm'
                      : 'tw-text-slate-600 hover:tw-text-slate-900'">
                    Notes
                  </button>
                </div>
              </div>
            </div>

            <div class="tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white tw-p-4 sm:tw-p-5">
              <div v-if="activeTab === 'overview'">
                <div class="tw-space-y-4">
                  <div class="tw-text-base tw-font-semibold tw-text-slate-900">Account Details</div>
                  <div class="tw-mt-3 tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                    <label class="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm tw-text-slate-700">
                      <input class="tw-h-4 tw-w-4 tw-rounded tw-border-slate-300" type="checkbox"
                        :checked="customer.isActive" @change="toggleCustomerActive($event)" />
                      Active
                    </label>
                  </div>
                  <div class="tw-mt-4 tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4">
                    <div class="tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-slate-50 tw-p-3">
                      <div class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500">Display
                        name</div>
                      <div v-if="isEditingDisplayName" class="tw-mt-2 tw-flex tw-flex-wrap tw-items-end tw-gap-2">
                        <InputField id="customerDisplayName" label="Display Name" class="tw-w-[320px]"
                          v-model="tempDisplayName"></InputField>
                        <Button
                          :isGreen="true"
                          :isPrimary="true"
                          class="tw-p-0"
                          icon="check"
                          type="button"
                          v-on:click="saveDisplayName"
                          title="Save display name"
                          aria-label="Save display name"
                        >
                          Save
                        </Button>
                        <Button
                          :isRed="true"
                          :isSecondary="true"
                          class="tw-p-0"
                          type="button"
                          v-on:click="cancelDisplayNameEdit"
                          title="Cancel display name edit"
                          aria-label="Cancel display name edit"
                        >
                          Cancel
                        </Button>
                      </div>
                      <div v-else class="tw-mt-2">
                        <div v-if="customer.displayName" class="tw-text-sm tw-font-medium tw-text-slate-900">{{
                          customer.displayName }}</div>
                        <div v-else class="tw-text-sm tw-text-slate-600">—</div>
                        <button type="button"
                          class="tw-mt-2 tw-inline-flex tw-items-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white/60 tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-white"
                          v-on:click="editDisplayName">
                          {{ customer.displayName ? 'Edit display name' : 'Add display name' }}
                        </button>
                      </div>
                    </div>

                    <div class="tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-slate-50 tw-p-3">
                      <div class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500">Contact
                        email</div>
                      <div v-if="isEditingEmail" class="tw-mt-2 tw-flex tw-flex-wrap tw-items-end tw-gap-2">
                        <InputField id="customerContactEmail" label="Email" class="tw-w-[320px]" v-model="contactEmail">
                        </InputField>
                        <Button
                          :isGreen="true"
                          :isPrimary="true"
                          class="tw-p-0"
                          icon="check"
                          type="button"
                          v-on:click="saveContactEmail"
                          title="Save contact email"
                          aria-label="Save contact email"
                        >
                          Save
                        </Button>
                        <Button
                          :isRed="true"
                          :isSecondary="true"
                          class="tw-p-0"
                          type="button"
                          v-on:click="cancelEditEmail"
                          title="Cancel contact email edit"
                          aria-label="Cancel contact email edit"
                        >
                          Cancel
                        </Button>
                      </div>
                      <div v-else class="tw-mt-2">
                        <div v-if="customer.contactEmail" class="tw-text-sm tw-font-medium tw-text-slate-900">{{
                          customer.contactEmail }}</div>
                        <div v-else class="tw-text-sm tw-text-slate-600">—</div>
                        <button type="button"
                          class="tw-mt-2 tw-inline-flex tw-items-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white/60 tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-white"
                          v-on:click="editEmail">
                          {{ customer.contactEmail ? 'Edit contact email' : 'Add contact email' }}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div class="tw-mt-4">
                    <!--
                    <a
                      class="tw-inline-flex tw-items-center tw-rounded-lg tw-border tw-border-transparent tw-bg-indigo-600 tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-indigo-700"
                      :href="customerPublicURL"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Customer Portal
                    </a>
                    -->
                  </div>
                </div>

                <div class="tw-mt-4 tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4">
                  <div class="tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white tw-p-4">
                    <div class="tw-text-sm tw-font-semibold tw-text-slate-900">Delivery Address</div>
                    <div class="tw-mt-3">
                      <div v-if="isEditingAddress">
                        <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
                          <GoogleAddressInput id="streetAddReceiving" label="Street Address" class="sm:tw-col-span-2"
                            v-model="editingCustomer.streetAddress" @address-selected="handleAddressSelection" />
                          <InputField id="suitNumberReceiving" label="Suite #" v-model="editingCustomer.suite">
                          </InputField>
                          <InputField id="cityReceiving" label="City" v-model="editingCustomer.city"></InputField>
                          <InputField id="stateReceiving" label="State" v-model="editingCustomer.state"></InputField>
                          <InputField id="zipReceiving" label="Zip" v-model="editingCustomer.zipCode"></InputField>
                        </div>
                        <div class="tw-mt-3 tw-flex tw-flex-wrap tw-gap-2">
                          <Button
                            :isGreen="true"
                            :isPrimary="true"
                            class="tw-p-0"
                            icon="check"
                            type="button"
                            v-on:click="saveAddress"
                            title="Save delivery address"
                            aria-label="Save delivery address"
                          >
                            Save
                          </Button>
                          <Button
                            :isRed="true"
                            :isSecondary="true"
                            class="tw-p-0"
                            type="button"
                            v-on:click="cancelAddressEdit"
                            title="Cancel delivery address edit"
                            aria-label="Cancel delivery address edit"
                          >
                            Cancel
                          </Button>
                          <button type="button"
                            class="tw-inline-flex tw-items-center tw-rounded-lg tw-border tw-border-rose-200 tw-bg-rose-50 tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-rose-900 hover:tw-bg-rose-100"
                            v-on:click="deleteDeliveryAddress">
                            Remove Address
                          </button>
                        </div>
                      </div>
                      <div v-else>
                        <div v-if="address">
                          <AddressBlock :name="customer.name" :address="customer.deliveryAddress"></AddressBlock>
                          <button
                            class="tw-mt-2 tw-inline-flex tw-items-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white/60 tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-white"
                            type="button" v-on:click="startAddressEdit">
                            Edit Address
                          </button>
                        </div>
                        <div v-else>
                          <div class="tw-text-sm tw-text-slate-600">No address on file.</div>
                          <button
                            class="tw-mt-2 tw-inline-flex tw-items-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white/60 tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-white"
                            type="button" v-on:click="startAddressEdit">
                            Add Address
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white tw-p-4">
                    <div class="tw-text-sm tw-font-semibold tw-text-slate-900">Billing Address</div>
                    <div class="tw-mt-3">
                      <div v-if="isEditingBillingAddress">
                        <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
                          <GoogleAddressInput id="billingStreet" label="Street Address" class="sm:tw-col-span-2"
                            v-model="editingCustomer.streetAddress" @address-selected="handleAddressSelection" />
                          <InputField id="billingSuite" label="Suite #" v-model="editingCustomer.suite"></InputField>
                          <InputField id="billingCity" label="City" v-model="editingCustomer.city"></InputField>
                          <InputField id="billingState" label="State" v-model="editingCustomer.state"></InputField>
                          <InputField id="billingZip" label="Zip" v-model="editingCustomer.zipCode"></InputField>
                        </div>
                        <div class="tw-mt-3 tw-flex tw-flex-wrap tw-gap-2">
                          <Button
                            :isGreen="true"
                            :isPrimary="true"
                            class="tw-p-0"
                            icon="check"
                            type="button"
                            v-on:click="saveBillingAddress"
                            title="Save billing address"
                            aria-label="Save billing address"
                          >
                            Save
                          </Button>
                          <Button
                            :isRed="true"
                            :isSecondary="true"
                            class="tw-p-0"
                            type="button"
                            v-on:click="cancelBillingAddressEdit"
                            title="Cancel billing address edit"
                            aria-label="Cancel billing address edit"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                      <div v-else>
                        <div v-if="billingAddress">
                          <AddressBlock :name="customer.name" :address="customer.billingAddress"></AddressBlock>
                          <button
                            class="tw-mt-2 tw-inline-flex tw-items-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white/60 tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-white"
                            type="button" v-on:click="startBillingAddressEdit">
                            Edit Billing Address
                          </button>
                        </div>
                        <div v-else>
                          <div class="tw-text-sm tw-text-slate-600">No billing address on file.</div>
                          <button
                            class="tw-mt-2 tw-inline-flex tw-items-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white/60 tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-white"
                            type="button" v-on:click="startBillingAddressEdit">
                            Add Billing Address
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-else-if="activeTab === 'containers'">
                <CustomerContainers :customer="customer"> </CustomerContainers>
              </div>

              <div v-else-if="activeTab === 'rates'">
                <CustomerRates :customer="customer"> </CustomerRates>
              </div>

              <div v-else-if="activeTab === 'contacts'">
                <CustomerContacts :customer="customer" @updateCustomer="refreshCustomer" />
              </div>

              <div v-else-if="activeTab === 'notes'">
                <AddNotesArea @addedNote="refreshCustomer" :notes="customer.notes" :archivedNotes="customer.archivedNotes" :customer="customer" type="customer">
                </AddNotesArea>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import Modal from '@/components/core/Modal/Modal.vue';
import AddNotesArea from '@/components/core/AddNotesArea.vue';
import UpdateCustomerName from '@/gql/customers/UpdateCustomerName.gql';
import EditCustomerActive from '@/gql/customers/EditCustomerActive.gql';
import UpdateCustomerAddress from '@/gql/customers/UpdateCustomerAddress.gql';

import EditCustomerName from '@/gql/customers/EditCustomerName.gql';
import EditDisplayName from '@/gql/customers/EditDisplayName.gql';

import CustomerQuery from '@/gql/customers/Customer.gql';
import AddCustomerContact from '@/gql/customers/AddCustomerContact.gql';
import UpdateCustomerContact from '@/gql/customers/UpdateCustomerContact.gql';
import EditCustomerEmail from '../../gql/customers/EditCustomerEmail.gql';

import AddressBlock from '../core/AddressBlock.vue';
import CustomerContacts from './CustomerContacts.vue';

import Button from '../core/Button.vue';
import InputField from '../core/Input/InputField.vue';
import { Contact } from '../../types';

import CustomerContainers from './CustomerContainers.vue';
import CustomerRates from './CustomerRates.vue';
import DeleteCustomerAddress from '@/gql/customers/DeleteCustomerAddress.gql';
// import CustomerOrders from './CustomerOrders.vue'

import GoogleAddressInput from '../core/GoogleAddressInput.vue';

import { pushToast } from '@/lib/toast';

export default defineComponent({
  name: 'CustomerDetails',
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    customerId: {
      required: true,
    },
  },
  emits: ['update:isOpen', 'close'],
  data() {
    return {
      activeTab: 'overview',
      editingContactId: -1,
      isEditingCustomer: false,
      isEditingName: false,
      tempCustomerName: '',
      isEditingEmail: false,
      isEditingDisplayName: false,
      tempDisplayName: '',
      isEditingAddress: false,
      isEditingBillingAddress: false,
      contactEmail: '',
      editingCustomer: {
        id: undefined,
        streetAddress: undefined,
        zipCode: undefined,
        city: undefined,
        state: undefined,
        suite: undefined,
      },
    };
  },
  components: {
    Modal,
    AddNotesArea,
    InputField,
    Button,
    AddressBlock,
    CustomerContacts,
    CustomerContainers,
    CustomerRates,
    GoogleAddressInput,
  },
  watch: {
    customerId() {
      const vm = this as any;
      vm.isEditingName = false;
      vm.tempCustomerName = '';
      vm.isEditingEmail = false;
      vm.contactEmail = '';
      vm.isEditingDisplayName = false;
      vm.tempDisplayName = '';
      vm.isEditingAddress = false;
      vm.isEditingBillingAddress = false;
      vm.resetEditingCustomer();
    },
  },
  computed: {
    customerPublicURL(): string {
      return `/status/orders/${btoa((this as any).customer.id)}`;
    },
    address(): string {
      if ((this as any).customer.deliveryAddress) {
        let address = '';
        const streetAddress = (this as any).customer.deliveryAddress
          .streetAddress;
        if (streetAddress) {
          address += streetAddress;
          address += '\n';
        }
        const city = (this as any).customer.deliveryAddress.city;
        if (city) {
          address += city;
          address += '\n';
        }
        const state = (this as any).customer.deliveryAddress.state;
        if (state) {
          address += state;
          address += '\n';
        }

        if ((this as any).customer.deliveryAddress.zipCode) {
          address += (this as any).customer.deliveryAddress.zipCode;
          address += '\n';
        }
        return address;
      } else {
        return '';
      }
    },
    billingAddress(): string {
      if ((this as any).customer.billingAddress) {
        let address = '';
        const streetAddress = (this as any).customer.billingAddress
          .streetAddress;
        if (streetAddress) {
          address += streetAddress;
          address += '\n';
        }
        const city = (this as any).customer.billingAddress.city;
        if (city) {
          address += city;
          address += '\n';
        }
        const state = (this as any).customer.billingAddress.state;
        if (state) {
          address += state;
          address += '\n';
        }
        if ((this as any).customer.billingAddress.zipCode) {
          address += (this as any).customer.billingAddress.zipCode;
          address += '\n';
        }
        return address;
      } else {
        return '';
      }
    },
  },

  methods: {
    getEmptyEditingCustomer() {
      return {
        id: undefined,
        streetAddress: undefined,
        zipCode: undefined,
        city: undefined,
        state: undefined,
        suite: undefined,
      };
    },
    resetEditingCustomer() {
      (this as any).editingCustomer = (this as any).getEmptyEditingCustomer();
    },
    handleAddressSelection(data: any) {
      const vm = (this as any);
      vm.editingCustomer.streetAddress = data.streetAddress;
      vm.editingCustomer.city = data.city;
      vm.editingCustomer.state = data.state;
      vm.editingCustomer.zipCode = data.zipCode;
    },
    onClose() {
      (this as any).$emit('close');
    },
    deleteDeliveryAddress() {
      const vm = (this as any);
  
      if (!confirm('Are you sure you want to remove the delivery address?')) return;

      vm.$apollo
        .mutate({
          mutation: DeleteCustomerAddress,
          variables: {
            customer_id: Number(vm.customer.id),
            is_billing: false,
          },
        })
        .then(() => {
          pushToast('Delivery address removed', 'success');
          vm.editingCustomer = {
            id: undefined,
            streetAddress: undefined,
            zipCode: undefined,
            city: undefined,
            state: undefined,
            suite: undefined,
          };
          vm.isEditingAddress = false;
          vm.refreshCustomer();
          vm.$emit('updateCustomer');
        })
        .catch((err: any) => {
          console.error(err);
          pushToast('Failed to remove address', 'error');
        });
    },
    toggleCustomerActive() {
      const vm = (this as any);
      const target = event.target as HTMLInputElement;
      const newValue = target.checked;

      vm.$apollo
        .mutate({
          mutation: EditCustomerActive,
          variables: {
            isActive: newValue,
            customerId: Number(vm.customer.id),
          },
        })
        .then((result) => {
          const serverStatus = result.data.editCustomerActive.customer.isActive;
          pushToast(`Customer ${serverStatus ? 'activated' : 'deactivated'}`, 'success');

          vm.refreshCustomer();
          vm.$emit('updateCustomer');
        })
        .catch((err) => {
          console.error(err);
          pushToast('Failed to update status', 'error');
          target.checked = !newValue;
        });
    },
    editEmail() {
      const vm = (this as any);

      vm.contactEmail = vm.customer.contactEmail;
      vm.isEditingEmail = true;
    },
    editDisplayName() {
      const vm = (this as any);
      vm.tempDisplayName = vm.customer.displayName || '';
      vm.isEditingDisplayName = true;
    },
    cancelEditEmail() {
      (this as any).isEditingEmail = false;
    },
    cancelDisplayNameEdit() {
      (this as any).isEditingDisplayName = false;
      (this as any).tempDisplayName = '';
    },
    editName() {
      const vm = (this as any);
      vm.tempCustomerName = vm.customer.name;
      vm.isEditingName = true;
    },
    cancelEditName() {
      (this as any).isEditingName = false;
      (this as any).tempCustomerName = '';
    },
    saveContactEmail() {
      const vm = (this as any);

      vm.$apollo
        .mutate({
          mutation: EditCustomerEmail,
          variables: {
            customerId: Number(vm.customer.id),
            email: vm.contactEmail,
          },
        })
        .then((data) => {
          vm.isEditingEmail = false;
          vm.refreshCustomer();
          vm.$emit('updateCustomer');
          pushToast('Email updated', 'success');
        })
        .catch((err) => {
          vm.isEditingEmail = false;
        });
    },
    saveCustomerName() {
      const vm = (this as any);

      vm.$apollo
        .mutate({
          mutation: EditCustomerName,
          variables: {
            customerId: Number(vm.customer.id),
            customerName: vm.tempCustomerName,
          },
        })
        .then((data) => {
          vm.isEditingName = false;
          vm.$emit('updateCustomer');
          vm.refreshCustomer();
          pushToast('Customer name updated', 'success');
        })
        .catch((err) => {
          vm.isEditingName = false;
          pushToast('Failed to update name', 'error');
        });
    },
    saveDisplayName() {
      const vm = (this as any);

      vm.$apollo
        .mutate({
          mutation: EditDisplayName,
          variables: {
            id: Number(vm.customer.id),
            displayName: vm.tempDisplayName,
          },
        })
        .then((data) => {
          vm.isEditingDisplayName = false;
          vm.refreshCustomer();
          pushToast('Display name updated', 'success');
        })
        .catch((err) => {
          vm.isEditingDisplayName = false;
          pushToast('Failed to update display name', 'error');
        });
    },
    startCustomerEdit() {
      (this as any).isEditingCustomer = true;
    },
    refreshCustomer() {
      (this as any).$apollo.queries.customer.refetch();
    },
    startAddressEdit() {
      const vm = (this as any);

      vm.isEditingAddress = true;
      vm.resetEditingCustomer();
      if (vm.customer.deliveryAddress) {
        vm.editingCustomer.streetAddress = vm.customer.deliveryAddress.streetAddress;
        vm.editingCustomer.suite = vm.customer.deliveryAddress.suite;
        vm.editingCustomer.city = vm.customer.deliveryAddress.city;
        vm.editingCustomer.state = vm.customer.deliveryAddress.state;
        vm.editingCustomer.zipCode = vm.customer.deliveryAddress.zipCode;
      }
    },
    cancelAddressEdit() {
      const vm = this as any;
      vm.isEditingAddress = false;
      vm.resetEditingCustomer();
    },
    startBillingAddressEdit() {
      const vm = (this as any);

      vm.isEditingBillingAddress = true;
      vm.resetEditingCustomer();
      if (vm.customer.billingAddress) {
        vm.editingCustomer.streetAddress = vm.customer.billingAddress.streetAddress;
        vm.editingCustomer.suite = vm.customer.billingAddress.suite;
        vm.editingCustomer.city = vm.customer.billingAddress.city;
        vm.editingCustomer.state = vm.customer.billingAddress.state;
        vm.editingCustomer.zipCode = vm.customer.billingAddress.zipCode;
      }
    },
    cancelBillingAddressEdit() {
      const vm = this as any;
      vm.isEditingBillingAddress = false;
      vm.resetEditingCustomer();
    },
    saveAddress() {
      const vm = (this as any);

      vm.$apollo
        .mutate({
          mutation: UpdateCustomerAddress,
          variables: {
            customerId: Number(vm.customer.id),
            streetAddress: vm.editingCustomer.streetAddress,
            suite: vm.editingCustomer.suite,
            city: vm.editingCustomer.city,
            state: vm.editingCustomer.state,
            zipCode: vm.editingCustomer.zipCode,
            isBilling: false,
          },
        })
        .then((data) => {
          vm.isEditingAddress = false;
          vm.$emit('updateCustomer');
          vm.editingCustomer = {
            id: undefined,
            streetAddress: undefined,
            zipCode: undefined,
            city: undefined,
            state: undefined,
            suite: undefined,
          };
          pushToast('Delivery address updated', 'success');
        })
        .catch((err) => {
          vm.isEditingAddress = false;
          pushToast('Failed to update address', 'error');
        });
    },
    saveBillingAddress() {
      const vm = (this as any);

      vm.$apollo
        .mutate({
          mutation: UpdateCustomerAddress,
          variables: {
            customerId: Number(vm.customer.id),
            streetAddress: vm.editingCustomer.streetAddress,
            suite: vm.editingCustomer.suite,
            city: vm.editingCustomer.city,
            state: vm.editingCustomer.state,
            zipCode: vm.editingCustomer.zipCode,
            isBilling: true,
          },
        })
        .then((data) => {
          vm.isEditingBillingAddress = false;
          vm.resetEditingCustomer();
          pushToast('Billing address updated', 'success');
        })
        .catch((err) => {
          vm.isEditingBillingAddress = false;
          pushToast('Failed to update billing address', 'error');
        });
    },
    selectOrder(orderId: number) {
      const vm = (this as any);
      vm.$emit('close');
      vm.$router.push(`/orders/${orderId}`);
    },
  },
  ...{
    apollo: {
      customer: {
        query: CustomerQuery,
        variables() {
          const vm = this as any;
          return {
            id: vm.customerId,
          };
        },
        skip() {
          return (this as any).customerId === -1;
        },
      },
    },
  },
} as any);
</script>

<style lang="scss">
.orderRow {
  cursor: pointer;
}
</style>
